import * as React from "react";
import type { Email } from "../../services/graphMailService";
import { useWindowManager } from "../windowManager/WindowManagerContext";
import { MailDetailContent, customerName } from "./MailDetailWindow";
import type { SidebarTabKey } from "./sidebar/sidebarTabs";
import { navKey, navPop, navPush, navTop, registriereMailFenster, type MailNavEntry } from "./mailDetailNav";

/**
 * J1 — Rahmen des Mail-Detail-FENSTERS.
 *
 * Aus der rechten Leiste heraus entsteht kein zweites Fenster: der Rahmen hält
 * einen Navigationsstapel und tauscht den Mailinhalt im SELBEN Fenster aus.
 * Zwei Dinge liegen deshalb hier und nicht im Inhalt:
 *
 *  - der **Stapel** (Zurück-Weg, Fenstertitel, Registrierung „welche Mail zeigt
 *    dieses Fenster gerade"),
 *  - der **geöffnete Reiter**: der Inhalt wird beim Mailwechsel bewusst über
 *    `key` remountet, damit alle mailbezogenen Effekte sauber neu greifen —
 *    der Reiter darf diesen Remount nicht mitmachen, sonst schlösse sich das
 *    Overlay bei jedem Klick und der Zurück-Pfeil wäre nie sichtbar.
 */

export type MailDetailWindowShellProps = {
    windowId: string;
    initial: MailNavEntry;
    onArchived?: (id: string) => void;
    singleMessage?: boolean;
    /** Verknüpfte Fenster behalten ihre Link-ID; andere Mails separat öffnen. */
    onOpenMailFromSidebar?: (mailbox: string, email: Email) => void;
};

/** Fenstertitel der aktuell gezeigten Mail — identisch zur Fenster-Fabrik. */
function fensterTitel(e: Email): string {
    return `${customerName(e)} — ${e.subject || "Kein Betreff"}`;
}

export const MailDetailWindowShell: React.FC<MailDetailWindowShellProps> = ({
    windowId,
    initial,
    onArchived,
    singleMessage,
    onOpenMailFromSidebar,
}) => {
    const { setWindowTitle } = useWindowManager();
    const [stapel, setStapel] = React.useState<MailNavEntry[]>([initial]);
    const [reiter, setReiter] = React.useState<SidebarTabKey | null>(null);
    const aktuell = navTop(stapel);
    const schluessel = navKey(aktuell);

    React.useEffect(() => {
        setWindowTitle(windowId, fensterTitel(aktuell.email));
    }, [windowId, aktuell, setWindowTitle]);

    // Registriert, WELCHE Mail dieses Fenster gerade zeigt — damit die Liste ein
    // bereits sichtbares Mail-Fenster fokussiert, statt ein zweites zu öffnen.
    React.useEffect(() => registriereMailFenster(windowId, schluessel), [windowId, schluessel]);

    return (
        <MailDetailContent
            key={schluessel}
            windowId={windowId}
            email={aktuell.email}
            mailbox={aktuell.mailbox}
            // A4 gilt nur für die ZUERST geöffnete Mail; aus der Leiste geöffnete
            // Mails sollen ihren Verlauf wieder auffächern.
            singleMessage={stapel.length === 1 ? singleMessage : false}
            onArchived={onArchived}
            aktiverReiter={reiter}
            onReiterWechsel={setReiter}
            onOpenMailFromSidebar={onOpenMailFromSidebar ?? ((mailbox, email) => setStapel((s) => navPush(s, { mailbox, email })))}
            onNavBack={stapel.length > 1 ? () => setStapel(navPop) : undefined}
        />
    );
};
